// HOME'S FIRST LOOK, KEPT ON THIS DEVICE (docs/SERVER_VIEWS.md, D2): the last atlas and wallet this browser read for a
// person, so their next visit draws Home from them at once, before the server answers. A server that does not hold the
// person loads their whole graph first (after a deploy, after hours idle); this way they never wait on it to see
// their own Home. It is read afresh behind it and replaced the moment the fresh one lands (lib/useWorld reloadAtlas).
// Only ever the person's own, on their own device; it leaves with them at sign-out (lib/storage removeUserKeys sweeps
// every kalibr key carrying their id).
import type { BareAtlas } from './atlas';
import type { WalletResponse } from './api/cards';

/** The kept copy's shape: a change to the atlas's wire shape bumps it, so an older copy is never drawn. */
const FORMAT = 1;
const keyOf = (userId: string) => `kalibrFirstLook:v${FORMAT}:${userId}`;
/** A copy this large is not kept (one origin's storage holds a few megabytes for every key): read afresh instead. */
const MOST_CHARS = 1_000_000;
/** A copy this old is not drawn: a person back after weeks sees their Home as it is, not as it was. */
const MOST_AGE_MS = 14 * 24 * 60 * 60 * 1000;

export type KeptWallet = Pick<WalletResponse, 'cards' | 'shown_order' | 'everything'>;
/** `tag`: the one the server named the atlas by (its ETag), so the next session asks only whether it has moved
 *  (lib/session, routes/session.js `have`). */
export interface FirstLook { atlas: BareAtlas; wallet: KeptWallet; at: number; tag?: string | null }

/** Keep the first look just read for this person, in place of the last. */
export function keepFirstLook(userId: string, atlas: BareAtlas, wallet: KeptWallet, tag: string | null = null): void {
  if (!userId) return;
  try {
    const text = JSON.stringify({ atlas, wallet: { cards: wallet.cards, shown_order: wallet.shown_order, everything: wallet.everything }, tag, at: Date.now() });
    if (text.length > MOST_CHARS) localStorage.removeItem(keyOf(userId));
    else localStorage.setItem(keyOf(userId), text);
  } catch { /* storage refused or full: the next visit reads afresh */ }
}

/** This person's kept first look, if this device holds a recent one in today's shape. */
export function keptFirstLook(userId: string, now = Date.now()): FirstLook | null {
  if (!userId) return null;
  try {
    // Older builds stored the full world. It has no reader now; retire this
    // person's obsolete snapshots without touching the atlas or other users.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('kalibrWorldKept:') && key.endsWith(`:${userId}`)) localStorage.removeItem(key);
    }
    const raw = localStorage.getItem(keyOf(userId));
    const look = raw ? (JSON.parse(raw) as FirstLook) : null;
    if (!look || !look.atlas || !Array.isArray(look.atlas.skills) || !Array.isArray(look.atlas.cards)) return null;
    if (!look.wallet || !Array.isArray(look.wallet.cards) || !(now - look.at < MOST_AGE_MS)) return null;
    return { ...look, tag: typeof look.tag === 'string' && look.tag ? look.tag : null };
  } catch {
    return null;
  }
}
